import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import Badge from '../../../components/Badge';
import { useQuery } from '@tanstack/react-query';
import { documentService } from '../../../services/central-api/document';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { GenericPaginatedParameters } from '../../../services/central-api/base-service';

interface Props {
    termsId: number;
    version: number;
}

export default function TermsAcceptedUsersList({ termsId, version }: Props) {
    const [filters, setFilters] = useState<GenericPaginatedParameters>({ page: 0, limit: 25 });
    const [textFilter, setTextFilter] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['getTermsOfUseAndPrivacyAcceptedUsers', termsId, version, filters],
        queryFn: () => documentService.getTermsOfUseAndPrivacyAcceptedUsers(termsId, version, filters),
    });

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <View style={{ flex: 1 }}>
            <DataTableHeader
                zIndex={2}
                title={translate('acceptances')}
                filterInput={<FilterInput
                    value={textFilter}
                    onChange={setTextFilter}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />

            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: translate('acceptance'),
                            cell: row => <View style={{ flex: 1, justifyContent: 'center', alignItems: 'flex-start' }}>
                                {row.status === 'accepted'
                                    ? <Badge mode='SQUARE' size='medium' color='green'>{translate('completed')}</Badge>
                                    : <Badge mode='SQUARE' size='medium' color='orange'>{translate('pending')}</Badge>}
                            </View>,
                        },
                        {
                            name: translate('user'),
                            selector: row => row.name,
                            wrap: true,
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBody ?? '-',
                            wrap: true,
                        },
                        {
                            name: translate('actingBodyUnit'),
                            selector: row => row.actingBodyUnit ?? '-',
                            wrap: true,
                        },
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => setFilters(old => ({ ...old, limit: pageSize, page: 0 }))}
                    setPage={page => setFilters(old => ({ ...old, page }))}
                />}
            />
        </View>
    );
}
